import { CampaignTag } from '@/legacy-types';
import { CampaignType } from '@/types';
import type { FilterDef } from '@alpine-iq/ui-library';

type CampaignStatus = 'draft' | 'active' | 'paused' | 'completed' | 'archived';

export type FiltersData = {
	types: CampaignType[];
	status: CampaignStatus[];
	startDate: string;
	endDate: string;
	channels: string[];
	pointsBoost: boolean;
	triggers: string[];
	tags: string[];
	archived: boolean;
};

export function getFilterDefs(tags?: Record<string, CampaignTag>): FilterDef[] {
	return [
		{
			label: 'Status',
			type: 'select-many',
			options: [
				{ label: 'Draft', value: 'draft' },
				{ label: 'Active', value: 'active' },
				{ label: 'Paused', value: 'inactive' },
				// { label: 'Completed', value: 'completed' },
				{ label: 'Archived', value: 'archived' },
			],
			key: 'status',
		},
		{
			label: 'Scheduled',
			type: 'date-range',
			keyDateStart: 'startDate',
			keyDateEnd: 'endDate',
		},
		{
			label: 'Channels',
			type: 'select-many',
			options: [
				{ label: 'Email', value: 'emailTemplate' },
				{ label: 'SMS', value: 'templates' },
				{ label: 'Push', value: 'pushTemplate' },
				{ label: 'In-App', value: 'browserTemplate' },
				{ label: 'Direct Mail', value: 'snailMailTemplate' },
				// { label: 'Web', value: 'adTemplate' },
			],
			key: 'channels',
		},

		{
			label: 'Type',
			type: 'select-many',
			options: [
				{ label: 'Regular', value: 'regular' },
				{ label: 'Waterfall', value: 'waterfall' },
				{ label: 'A/B', value: 'variant' },
			],
			key: 'types',
		},
		{
			label: 'Tags',
			type: 'select-many',
			loading: !tags,
			options: Object.values(tags ?? {}).map((tag) => ({ label: tag.name, value: tag.id })),
			key: 'tags',
		},
		{
			label: 'Triggers',
			type: 'select-many',
			options: [
				{ label: 'Post Sale', value: 'postSale' },
				{ label: 'Abandoned Cart', value: 'abandonedCart' },
				// { label: 'Geo-Fenced', value: 'geoFenced' },
				{ label: 'Birthday', value: 'birthday' },
			],
			key: 'triggers',
		},
		{
			label: 'Points Boost',
			type: 'toggle',
			key: 'pointsBoost',
		},
		{
			label: 'Archived',
			type: 'toggle',
			key: 'archived',
		},
	];
}

// Short keys for URL parameters to keep URLs concise
const filterKeys: { [key in keyof FiltersData]?: string } = {
	status: 's',
	startDate: 'scs',
	endDate: 'sce',
	channels: 'c',
	tags: 't',
};

// Encode value as Base64 for complex data types (arrays, strings) and use booleans as 1 or 0
function encodeValue(value: any): string {
	if (typeof value === 'boolean') {
		return value ? '1' : '0';
	}
	return btoa(JSON.stringify(value)); // Base64 encode for arrays and strings
}

// Decode Base64 values or boolean encoded values
function decodeValue(value: string, isBoolean = false): any {
	if (isBoolean) {
		return value === '1';
	}
	return JSON.parse(atob(value)); // Base64 decode
}

// Update the URL with compact, encoded parameters
export const updateURL = (filters: Partial<FiltersData>) => {
	const params = new URLSearchParams();
	for (const [key, value] of Object.entries(filters)) {
		const shortKey = filterKeys[key as keyof FiltersData];
		if (shortKey && value !== undefined && value !== null) {
			if (key === 'archived') {
				params.append(shortKey, encodeValue(value)); // Encode boolean directly
			} else {
				params.append(shortKey, encodeValue(value)); // Encode complex types as Base64
			}
		}
	}
	const newURL = new URL(window.location.href);
	newURL.search = params.toString();
	window.history.replaceState(null, '', newURL.toString());
};

// Retrieve and decode filters from the URL parameters
export const getFiltersFromURL = (): Partial<FiltersData> => {
	const params = new URLSearchParams(window.location.search);
	const filters: Partial<FiltersData> = {};

	const reverseFilterKeys = Object.entries(filterKeys).reduce((acc, [key, value]) => ({ ...acc, [value]: key }), {} as Record<string, string>);

	params.forEach((value, shortKey) => {
		const key = reverseFilterKeys[shortKey];
		if (key) {
			try {
				if (key === 'archived') {
					filters[key as keyof FiltersData] = decodeValue(value, true); // Decode boolean
				} else {
					filters[key as keyof FiltersData] = decodeValue(value); // Decode Base64 for complex types
				}
			} catch (e) {
				console.warn(`Failed to parse URL parameter: ${shortKey}=${value}`);
			}
		}
	});

	return filters;
};
